import { toast } from '@/lib/toast';
import { useUpdateNurseryPrice } from '@/hooks/useProducts';
import { InlineEditCell } from './InlineEditCell';

interface NurseryPriceEditorProps {
  /** The variant the nursery price belongs to. */
  shopifyVariantId: number | null;
  /** The current nursery price, or null if unset. */
  value: number | null;
  className?: string;
}

/**
 * Click-to-edit "At Nursery" price cell.
 *
 * Self-contained: saving writes `variants.nursery_price` locally and pushes the
 * value to the Shopify `custom_fields.offline_price` metafield. This is the one
 * nursery-price editor — the Products list, Variants list and product detail
 * page all use it, so editing behaves identically everywhere. The click-to-edit
 * mechanics live in the shared {@link InlineEditCell}.
 */
export function NurseryPriceEditor({
  shopifyVariantId,
  value,
  className,
}: NurseryPriceEditorProps) {
  const update = useUpdateNurseryPrice();

  const commit = (next: string | number | null) => {
    if (shopifyVariantId == null) {
      toast.error('This product has no variant to price');
      return;
    }
    const price = next == null || next === '' ? null : Number(next);
    update.mutate(
      { shopifyVariantId, price },
      {
        onSuccess: () =>
          toast.success(
            price == null
              ? 'Nursery price cleared in Shopify'
              : 'Nursery price saved to Shopify',
          ),
        onError: (err) =>
          toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
      },
    );
  };

  return (
    <InlineEditCell
      value={value}
      kind="currency"
      onCommit={commit}
      pending={update.isPending}
      emptyLabel="Set price"
      className={className}
    />
  );
}
